import { DELIMITER, OTHER_DELIMITER_ID, delimiterLocale, delimiterSymbol } from "Common/dictionaries/delimiter";
import { SORT_ORDER } from "Common/dictionaries/sortOrder";
import DataGridBase from "Components/dataGridBase";
import { CELL_NAME_FOR_STORE_KEY_FIELD } from "Components/dataGridBase/utils";
import { NumberWidget, TextWidget } from "Components/optionForm";
import { CheckBox } from "devextreme-react/check-box";
import { Column } from "devextreme-react/data-grid";
import filter from "lodash/filter";
import forEach from "lodash/forEach";
import map from "lodash/map";
import reduce from "lodash/reduce";
import some from "lodash/some";
import split from "lodash/split";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { IMPORT_PARAMS_DEFAULT, MAX_ROWS_PREVIEW_IMPORT } from "../../constants";
import locale from "./importForm.locale";
import CheckRadioButton from "Components/checkBoxRadio";
const availableDelimiters = [
    ...map(DELIMITER, d => ({
        Id: d,
        Symbol: delimiterSymbol[d],
        Name: delimiterLocale[d]
    })),
    { Id: OTHER_DELIMITER_ID, Symbol: undefined, Name: locale.OTHER_DELIMITER }
];
const keyExpr = "ID";
export default function ImportForm({ importFormRef }) {
    const [previewFields, setPreviewFields] = useState([]);
    const [previewData, setPreviewData] = useState([]);
    const [previewLines, setPreviewLines] = useState([]);
    const [startLine, setStartLine] = useState(IMPORT_PARAMS_DEFAULT.startLine);
    const [containsColumnNames, setContainsColumnNames] = useState(IMPORT_PARAMS_DEFAULT.containsColumnNames);
    const [canUseContainsColumnNames, setCanUseContainsColumnNames] = useState(true);
    const [delimiter, setDelimiter] = useState(IMPORT_PARAMS_DEFAULT.delimiter);
    const [delimiterId, setDelimiterId] = useState(IMPORT_PARAMS_DEFAULT.delimiterId);
    const reset = useCallback(() => {
        setStartLine(IMPORT_PARAMS_DEFAULT.startLine);
        setContainsColumnNames(IMPORT_PARAMS_DEFAULT.containsColumnNames);
        setDelimiter(IMPORT_PARAMS_DEFAULT.delimiter);
        setDelimiterId(IMPORT_PARAMS_DEFAULT.delimiterId);
        setCanUseContainsColumnNames(IMPORT_PARAMS_DEFAULT.canUseContainsColumnNames);
    }, []);
    useEffect(() => {
        if (importFormRef) {
            importFormRef.current = {
                reset,
                setPreviewLines,
                getImportParams: () => ({
                    startLine,
                    containsColumnNames,
                    delimiter
                })
            };
        }
    }, [startLine, containsColumnNames, delimiter]);
    const makeField = useCallback((i) => `${locale.FIELD} ${i}`, []);
    useEffect(() => {
        if (delimiter !== "") {
            const fields = [];
            const tableData = [];
            const offset = startLine - 1 || 0;
            const rows = previewLines
                .map(line => split(line, delimiter))
                .slice(offset, offset + MAX_ROWS_PREVIEW_IMPORT);
            let fieldCount = 0;
            forEach(rows, (row, id) => {
                if (Number(id) === 0 && containsColumnNames) {
                    fields.push(...row);
                }
                else {
                    const item = reduce(row, (acc, col, i) => {
                        acc[fields[i] || makeField(i)] = col;
                        return acc;
                    }, {});
                    fieldCount = row.length > fieldCount ? row.length : fieldCount;
                    tableData.push({ [keyExpr]: id, ...item });
                }
            });
            if (fieldCount > 0 && fields.length < fieldCount) {
                const row = map(new Array(fieldCount), (field, index) => makeField(index) || field);
                fields.push(...row);
            }
            if (some(fields, field => !field)) {
                setContainsColumnNames(false);
                setCanUseContainsColumnNames(false);
            }
            else {
                setPreviewFields(fields);
                setPreviewData(tableData);
            }
        }
    }, [
        delimiterId,
        delimiter,
        containsColumnNames,
        startLine,
        previewLines,
        canUseContainsColumnNames
    ]);
    const tableKey = useMemo(() => uuid(), [previewData]);
    const handleContainsColumnNamesChanged = useCallback(({ value, event }) => {
        event && setContainsColumnNames(value);
    }, []);
    const handleOtherDelimiterChanged = useCallback((value) => {
        value !== "" && setDelimiterId(OTHER_DELIMITER_ID);
        setDelimiter(value);
        setCanUseContainsColumnNames(IMPORT_PARAMS_DEFAULT.canUseContainsColumnNames);
    }, []);
    return (React.createElement("div", { className: "flex-single-column" },
        React.createElement("div", { className: "mb-gap" },
            React.createElement("div", { className: "d-flex v-center-items" },
                React.createElement(NumberWidget, { onChange: setStartLine, value: startLine, min: 1, width: 166, className: "mr-1", label: locale.START_LINE, labelMode: "floating" }),
                React.createElement(CheckBox, { text: locale.CONTAINS_COLUMN_NAMES, value: containsColumnNames, onValueChanged: handleContainsColumnNamesChanged, disabled: !canUseContainsColumnNames }))),
        React.createElement("div", { className: "mb-gap" },
            React.createElement("div", { className: "font-base font-bold pb-1" }, locale.DELIMITER),
            React.createElement("div", { className: "flex-space-between" },
                useMemo(() => map(availableDelimiters, item => (React.createElement(CheckRadioButton, { key: item.Id, item: {
                        Key: String(item.Id),
                        Name: item.Name
                    }, checked: delimiterId === item.Id, className: "mr-0_25", onCheckedChanged: () => {
                        setDelimiterId(item.Id);
                        item.Symbol && setDelimiter(item.Symbol);
                        setCanUseContainsColumnNames(IMPORT_PARAMS_DEFAULT.canUseContainsColumnNames);
                    } }))), [delimiterId]),
                React.createElement(TextWidget, { label: locale.SYMBOL, labelMode: "floating", width: 118, height: 36, value: delimiter, onChange: handleOtherDelimiterChanged }))),
        React.createElement("div", { className: "flex-greedy-block grid-rows-auto-1fr overflow-hidden" },
            React.createElement("div", { className: "font-base font-bold pb-1" }, locale.PREVIEW),
            React.createElement(DataGridBase, { showBorders: true, allFilterPanelsOn: false, highlightUpdates: false, key: tableKey, data: previewData, keyExpr: keyExpr, minBottomOffset: 110, minRowHeight: 35 },
                React.createElement(Column, { dataField: keyExpr, sortOrder: SORT_ORDER.ASC, visible: false, name: CELL_NAME_FOR_STORE_KEY_FIELD, allowHiding: false, showInColumnChooser: false }),
                filter(previewFields, (field) => field !== keyExpr).map((field) => (React.createElement(Column, { key: field, allowHeaderFiltering: false, dataField: field, dataType: "string", caption: field, width: "auto", minWidth: 70 })))))));
}
